'use client'

import { useEffect } from 'react'

type CapWindow = Window & {
  Capacitor?: {
    isNativePlatform?: () => boolean
    getPlatform?: () => string
    Plugins?: {
      StatusBar?: {
        setOverlaysWebView?: (opts: { overlay: boolean }) => Promise<void>
        setStyle?: (opts: { style: string }) => Promise<void>
        setBackgroundColor?: (opts: { color: string }) => Promise<void>
      }
    }
  }
}

function readSafeInset(prop: 'top' | 'bottom') {
  const probe = document.createElement('div')
  probe.style.position = 'fixed'
  probe.style.visibility = 'hidden'
  probe.style.paddingTop = prop === 'top' ? 'env(safe-area-inset-top, 0px)' : '0'
  probe.style.paddingBottom = prop === 'bottom' ? 'env(safe-area-inset-bottom, 0px)' : '0'
  document.body.appendChild(probe)
  const styles = getComputedStyle(probe)
  const value =
    prop === 'top'
      ? Number.parseFloat(styles.paddingTop) || 0
      : Number.parseFloat(styles.paddingBottom) || 0
  document.body.removeChild(probe)
  return value
}

// Match the top bar background so the status bar blends in.
// Values mirror --ds-surface in design-tokens.css.
const TOPBAR_BG = { light: '#ffffff', dark: '#111827' }

function isDarkTheme() {
  return document.documentElement.getAttribute('data-bs-theme') === 'dark'
}

async function applyStatusBarStyle() {
  const cap = (window as CapWindow).Capacitor
  const statusBar = cap?.Plugins?.StatusBar
  if (!statusBar) return

  const dark = isDarkTheme()
  try {
    await statusBar.setOverlaysWebView?.({ overlay: true })
    // Capacitor's iOS style names describe the *background*, not the text:
    //   'DARK'  -> light (white) icons  → use on a dark background
    //   'LIGHT' -> dark icons           → use on a light background
    await statusBar.setStyle?.({ style: dark ? 'DARK' : 'LIGHT' })
    await statusBar.setBackgroundColor?.({ color: dark ? TOPBAR_BG.dark : TOPBAR_BG.light })
  } catch {
    // StatusBar plugin may not be ready on first paint
  }
}

export default function CapacitorSafeArea() {
  useEffect(() => {
    const cap = (window as CapWindow).Capacitor
    const isNative = Boolean(cap?.isNativePlatform?.() ?? cap)
    if (!isNative) return

    document.documentElement.classList.add('capacitor-native')

    const apply = async () => {
      await applyStatusBarStyle()

      const envTop = readSafeInset('top')
      const envBottom = readSafeInset('bottom')
      const top = Math.max(envTop, 59)
      const bottom = Math.max(envBottom, 34)

      document.documentElement.style.setProperty('--app-safe-top', `${top}px`)
      document.documentElement.style.setProperty('--app-safe-bottom', `${bottom}px`)
    }

    void apply()
    const timers = [50, 300, 800].map((ms) => window.setTimeout(() => void apply(), ms))

    // Re-apply the status bar style whenever the theme (data-bs-theme) changes.
    const observer = new MutationObserver(() => {
      void applyStatusBarStyle()
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })

    return () => {
      timers.forEach(clearTimeout)
      observer.disconnect()
    }
  }, [])

  return null
}
